<script setup lang="ts">
import {
	computed,
	ref
} from 'vue'
import { useFileDrop, MAX_FILES_PER_BATCH } from "@/composables/useFileDrop";
import { useNotification } from "@/composables/useNotification";
import { useUploadQueue } from "@/composables/useUploadQueue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import FileInfoModal from "@/components/file/modals/FileInfoModal.vue";
import JobInfoModal from "@/components/main/jobs-panel/modals/JobInfoModal.vue";
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue";
import {
	Job,
	JobFile,
	SimpleName,
	jobsStore
} from "@/store/jobs";
import {JobStatus} from "@/models/etc/types";
import {FileApi} from "@/ api/FileApi";
import {workerStore} from "@/store/worker";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import JobsOptionsDropdown from "@/components/main/jobs-panel/dropdowns/JobsOptionsDropdown.vue";
import CreateEditJobDropdown from "@/components/main/jobs-panel/dropdowns/CreateEditJobDropdown.vue";
import DeleteJobConfirmDropdown from "@/components/main/jobs-panel/dropdowns/DeleteJobConfirmDropdown.vue";
import {
	highlightSearchText,
	escapeHtml,
	splitMessageByBr,
	stripHighlightMarkup,
	trimTrailingLineBreaks
} from "@/utils/textHelpers";
import { i18n, tp } from "@/i18n";
import {useRoute, useRouter} from "vue-router";
import {TagEntityType} from "@/models/etc/types";
import type {TagWithFrequency} from "@/models/interfaces/tags/tag-models";
import {searchStore} from "@/store/search";
import {splitTextByPhoneNumbers} from "@/utils/contactsHelpers";
import {normalizeUrlForOpening} from "@/utils/urlHelpers";
import {PersonApi} from "@/ api/PersonApi";
import ContactsEmailPhoneOptionsDropdown
	from "@/components/main/clients-panel/dropdowns/ContactsEmailPhoneOptionsDropdown.vue";
import {useJobsAccess} from "@/composables/useJobsAccess";
import {useJobWorkerOptions} from "@/composables/useJobWorkerOptions";
import {useMessageSendingWay} from "@/composables/useMessageSendingWay";
import {getJobDeadlineState} from "@/utils/jobDeadline";
import { projectPageStore } from "@/store/project-page";
import { clientPageStore } from "@/store/client-page";

const route = useRoute()
const router = useRouter()

const t = (key: string, named?: Record<string, unknown>): string => {
	return String(named ? i18n.global.t(key, named) : i18n.global.t(key))
}

const {isJobsReadonly} = useJobsAccess()
const projectStore = projectPageStore()
const clientStore = clientPageStore()

const fileAccessLevel = computed(() => {
	if (route.query.page === 'client') {
		return clientStore.openedClient?.FileAccess ?? 0
	}
	if (route.query.page === 'project') {
		return projectStore.openedProject?.FileAccess ?? 0
	}
	return 0
})

const hasFileAccessContext = computed(
	() => route.query.page === 'client' || route.query.page === 'project'
)

const hasFileAccess = computed(
	() => !hasFileAccessContext.value || fileAccessLevel.value !== 0 || isAdmin.value
)

const canManageJobFiles = computed(
	() => !hasFileAccessContext.value || fileAccessLevel.value === 2 || isAdmin.value
)

const MAX_SIZE_MB = 100

const props = defineProps<{
	job: Job
}>();

const $job = ref<HTMLElement>()
const $uploadFile = ref<HTMLInputElement>()
const $fileInfoModal = ref<typeof FileInfoModal | null>(null);
const $jobInfoModal = ref<InstanceType<typeof JobInfoModal> | null>(null);
const $optionsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $editJobDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $deleteJobDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $addTagDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const store = jobsStore()
const searchStoreInstance = searchStore()
const isLoading = ref(false);
const showAllFiles = ref(false);

const notify = useNotification()
const uploadQueue = useUploadQueue()

const {
	isDraggingOver,
	canDrop,
	validateFile,
	handleDragOver,
	handleDragEnter,
	handleDragLeave,
	handleDrop,
} = useFileDrop({
	maxSizeMb: MAX_SIZE_MB,
	maxFiles: MAX_FILES_PER_BATCH,
	multiple: true,
	onError: (message) => notify.danger(t('files_label'), message),
	onFiles: async (files) => {
		enqueueDeviceUpload(files)
	},
	onProjectFiles: async (files) => {
		await attachProjectFiles(files)
	},
})

const jobTagsModel = computed<TagWithFrequency[]>(
	() => (props.job.Tags ?? []) as unknown as TagWithFrequency[]
)

const tagsReadonlyText = computed(() =>
	(props.job.Tags ?? []).map(t => t.Name.toLowerCase()).join(', ')
)

const hasAttachments = computed(() => !!props.job.Files?.length || !!props.job.Tags?.length)

const isTagDropdownOpen = ref(false)

const onTagSelectorChange = async (nextTags: TagWithFrequency[]) => {
	if (isJobsReadonly.value) {
		return
	}
	const prevIds = new Set((props.job.Tags ?? []).map(t => t.Id))
	const nextIds = new Set(nextTags.map(t => t.Id))
	const added = nextTags.filter(t => !prevIds.has(t.Id))
	const removedIds = [...prevIds].filter(id => !nextIds.has(id))
	try {
		for (const tag of added) {
			await store.attachTagToJob(props.job.Id, tag.Name)
		}
		for (const id of removedIds) {
			await store.detachTagFromJob(props.job.Id, id)
		}
	} catch (e) {
		console.error(e)
	}
}

const onCreateTag = async (name: string) => {
	if (isJobsReadonly.value) {
		return
	}
	try {
		await store.attachTagToJob(props.job.Id, name)
	} catch (e) {
		console.error(e)
	}
}

const onDetachTag = async (tagId: number) => {
	if (isJobsReadonly.value) {
		return
	}
	try {
		await store.detachTagFromJob(props.job.Id, tagId)
	} catch (e) {
		console.error(e)
	}
}

const { workersOptions: optionsPersons } = useJobWorkerOptions()

const handleEdit = () => {
	$optionsDropdown.value?.close()
	$editJobDropdown.value?.open()
}

const enqueueDeviceUpload = (files: File[]) => {
	uploadQueue.enqueue(files, (file, hooks) =>
		store.attachJobFileFromDevice(file, props.job.Id, hooks)
	)
}

const notifyAttachResult = (ok: number, fail: number) => {
	if (ok > 0 && fail === 0) {
		notify.success(t('files_label'), tp('file_attached_count', ok))
	} else if (ok > 0 && fail > 0) {
		notify.warning(t('files_label'), t('file_attach_partial', { ok, fail }))
	} else if (fail > 0) {
		notify.danger(t('files_label'), tp('file_attach_failed_count', fail))
	}
}

const attachProjectFiles = async (files: { Id: number; Name: string; Signed: boolean }[]) => {
	let ok = 0
	let fail = 0
	try {
		isLoading.value = true
		for (const f of files) {
			try {
				await store.attachJobFileFromClientOrProject(props.job.Id, f.Id, {
					Id: f.Id,
					Name: f.Name,
					Signed: f.Signed,
				})
				ok++
			} catch (e) {
				console.error(e)
				fail++
			}
		}
	} finally {
		isLoading.value = false
	}
	notifyAttachResult(ok, fail)
}

const detachFile = async (file: JobFile) => {
	if (isJobsReadonly.value) {
		return
	}
	try {
		isLoading.value = true
		await store.detachJobFile(props.job.Id, file.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const showFileInfo = async (file: JobFile) => {
	if (!hasFileAccess.value) return

	try {
		isLoading.value = true
		const containerFile = await FileApi.GetFileInfo(file.Id);
		if (containerFile) {
			$fileInfoModal.value?.onOpen(
				containerFile,
				"file",
				undefined,
				undefined,
				file.Editable ?? false
			);
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const downloadFile = (file: JobFile) => {
	if (!hasFileAccess.value || file.Editable === false) return

	try {
		FileApi.FileDownload(file.Id);
	} catch (e) {
		console.error(e)
	}
}

const getFileLocationTarget = (job: Job): { page: 'project' | 'client', id: number } | null => {
	if (job.Project) {
		return { page: 'project', id: job.Project.Id }
	}
	const firstClientId = job.Clients?.[0]?.Id
	if (firstClientId && firstClientId !== 1) {
		return { page: 'client', id: firstClientId }
	}
	return null
}

const goToFileLocation = (file: JobFile) => {
	if (!hasFileAccess.value) return

	const target = getFileLocationTarget(props.job)
	if (!target) return
	searchStoreInstance.requestFileSearch({
		type: target.page,
		id: target.id,
		fileName: file.Name,
		fileId: file.Id
	})
	const alreadyThere = route.query.page === target.page && Number(route.query.id) === target.id
	if (!alreadyThere) {
		router.push({ query: { ...route.query, page: target.page, id: target.id } })
	}
}

const triggerFileInput = () => {
	$uploadFile.value?.click()
}

const handleClick = () => {
	if (isJobsReadonly.value || !canManageJobFiles.value) {
		return
	}
	triggerFileInput()
}

const handleFileInput = async (event: Event) => {
	const target = event.target as HTMLInputElement
	if (!target.files || target.files.length === 0) {
		return
	}

	const accepted: File[] = []
	for (const file of Array.from(target.files)) {
		const validation = validateFile(file)
		if (!validation.isValid) {
			notify.danger(t('files_label'), validation.error!)
			continue
		}
		accepted.push(file)
	}
	target.value = ''
	if (accepted.length === 0) {
		return
	}

	let files = accepted
	if (files.length > MAX_FILES_PER_BATCH) {
		files = files.slice(0, MAX_FILES_PER_BATCH)
		notify.warning(t('files_label'), t('file_batch_limit', { count: MAX_FILES_PER_BATCH }))
	}
	enqueueDeviceUpload(files)
}

const showUserJobs = (userId: number) => {
	store.setUserForUpdateJobsList(userId)
}

const getJobStatusBarColor = (status: JobStatus) => {
	switch (status) {
		case JobStatus.Standard:
			return "#9A9C9E";
		case JobStatus.Working:
			return "#86CED2";
		case JobStatus.WaitingForCheck:
			return "#FFE7AA";
		case JobStatus.Completed:
			return "#6DA279";
		case JobStatus.Lost:
			return "#EE966A";
	}
}

const getSelfJobStatusBarColor = (status: JobStatus) => {
	switch (status) {
		case JobStatus.Standard:
			return "#97B2DE";
		case JobStatus.Working:
			return "#F1AC98";
		case JobStatus.WaitingForCheck:
			return "#C7C8C9";
		case JobStatus.Completed:
			return "#99BEA1";
		case JobStatus.Lost:
			return "#F6DB91";
	}
}

const jobStatusText = computed(() => {
	switch (props.job.Status) {
		case JobStatus.Standard:
			return t('job_state_system');
		case JobStatus.Working:
			return t('job_in_progress');
		case JobStatus.WaitingForCheck:
			return t('job_state_checking');
		case JobStatus.Completed:
			return t('job_state_completed');
		case JobStatus.Lost:
			return t('job_state_urgent');
		default:
			return "";
	}
})

const toDate = (date?: string | Date) => {
	if (!date) {
		return null;
	}
	return typeof date === "string" ? new Date(date) : date;
}

const formatDate = (date?: string | Date) => {
	const dateObj = toDate(date)
	if (!dateObj) {
		return "";
	}

	// Формат dd.MM.yy намеренно не зависит от локали: en-US дал бы MM/DD/YY,
	// и день с месяцем читались бы наоборот.
	const day = String(dateObj.getDate()).padStart(2, '0');
	const month = String(dateObj.getMonth() + 1).padStart(2, '0');
	const year = String(dateObj.getFullYear()).slice(-2);
	return `${day}.${month}.${year}`;
}

const formatDateTime = (date?: string | Date) => {
	const dateObj = toDate(date)
	if (!dateObj) {
		return "";
	}

	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');
	return `${formatDate(dateObj)} ${hours}:${minutes}`;
}

const formatTimeDate = (date?: string | Date) => {
	const dateObj = toDate(date)
	if (!dateObj) {
		return "";
	}

	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');
	return `${hours}:${minutes} ${formatDate(dateObj)}`;
}

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

type JobAction = 'endByWorker' | 'sendToChecking' | 'sendToReworking' | 'endByAdmin'

const worker = workerStore()
const { newLineMode, sendHintKey } = useMessageSendingWay()

const showCommentInput = ref(false)
const commentMessage = ref('')
const pendingAction = ref<JobAction | null>(null)
const additionsExpanded = ref(false)
const $commentTextarea = ref<InstanceType<typeof AppCustomTextarea> | null>(null)

const isClientsExpanded = ref(false)

const isProjectContext = computed(() => store.context.type === 'project')

// На странице проекта клиентов не показываем. На странице клиента — только с фильтром
// «С холдингом» и без текущего клиента: остаются чужие клиенты холдинга
const visibleClients = computed<SimpleName[]>(() => {
	const clients = props.job.Clients ?? []
	const ctx = store.context
	if (ctx.type === 'project') {
		return []
	}
	if (ctx.type === 'client') {
		if (!ctx.holdingSearch) {
			return []
		}
		return clients.filter(client => client.Id !== ctx.entityId)
	}
	return clients
})

const displayedClients = computed(() =>
	isClientsExpanded.value
		? visibleClients.value
		: visibleClients.value.slice(0, 1)
)

const userId = computed(() => worker.workerProfile?.Id)
const isAdmin = computed(() => worker.workerProfile?.IsAdmin ?? false)
const isCreator = computed(() => props.job.Creator?.Id === userId.value)
const isWorker = computed(() => props.job.Worker?.Id === userId.value)
const isSystemJob = computed(() => props.job.Status === JobStatus.Standard)

const dateState = computed(() => getJobDeadlineState(props.job))
const isDateLost = computed(() => dateState.value === 'lost')
const isDateSuccess = computed(() => dateState.value === 'success')

const dateTooltip = computed(() => {
	if (!props.job.CompletionDate) {
		return t('job_deadline')
	}
	// У завершённой работы на месте срока показана дата завершения, поэтому
	// исходный срок выносим в тултип
	return isDateLost.value
		? t('job_overdue_at', { date: formatDate(props.job.ToDoDate) })
		: t('job_state_completed')
})

const showCreatorName = computed(() =>
	!!props.job.Creator && props.job.Creator.Id !== props.job.Worker?.Id
)

const canEditJob = computed(() =>
	isCreator.value && props.job.Changeable && !props.job.FinishedByAdmin
)
const canDeleteJob = computed(() =>
	(isCreator.value && props.job.Changeable)
	|| (isAdmin.value && !(isCreator.value && props.job.Changeable))
)

const isWorkingOrLost = computed(() =>
	props.job.Status === JobStatus.Working || props.job.Status === JobStatus.Lost
)

const isWaitingForCheck = computed(() =>
	props.job.Status === JobStatus.WaitingForCheck
)

// Случай 1: Исполнитель, с отчётом (sendToChecking)
const showSendToChecking = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && !isCreator.value && props.job.NeedChecking
)

// Случай 2: Исполнитель, без отчёта (endByWorker)
const showEndByWorkerNoMessage = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && !isCreator.value && !props.job.NeedChecking
)

// Случай 3: Исполнитель = Создатель, с отчётом (endByWorker с сообщением ИЛИ без)
const showSelfWorkerWithReport = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && isCreator.value && props.job.NeedChecking
)

// Случай 4: Исполнитель = Создатель, без отчёта (endByWorker без сообщения)
const showSelfWorkerNoReport = computed(() =>
	!showCommentInput.value && isWorkingOrLost.value && isWorker.value && isCreator.value && !props.job.NeedChecking
)

// Случай 5: Инициатор проверяет (endByCreator + sendToReworking)
const showCreatorChecking = computed(() =>
	!showCommentInput.value && isWaitingForCheck.value && isCreator.value && !isWorker.value
)

// Случай 6: Админ завершает (чистый админ — не creator и не worker)
const showAdminEnd = computed(() =>
	!showCommentInput.value && isAdmin.value && !isCreator.value && !isWorker.value
	&& (isWorkingOrLost.value || isWaitingForCheck.value)
)

// Админ + участник работы: кнопка FinishAsAdmin добавочная к основным
// (a) Worker+Admin+NeedChecking+Working/Lost — рядом с SendToChecking
// (b) Creator(only)+Admin+Working/Lost — одиночная кнопка
// (c) Worker+Admin+NeedChecking+Checking
const showAdminEndExtra = computed(() => {
	if (showCommentInput.value || !isAdmin.value) return false
	if (isWorkingOrLost.value && isWorker.value && !isCreator.value && props.job.NeedChecking) return true
	if (isWorkingOrLost.value && isCreator.value && !isWorker.value) return true
	if (isWaitingForCheck.value && isWorker.value && !isCreator.value && props.job.NeedChecking) return true
	return false
})

const hasActionButtons = computed(() =>
	showSendToChecking.value || showEndByWorkerNoMessage.value || showSelfWorkerWithReport.value
	|| showSelfWorkerNoReport.value || showCreatorChecking.value
)

const commentLabel = computed(() => {
	switch (pendingAction.value) {
		case 'endByWorker': return `${t('job_comment_label_finish')}:`
		case 'sendToChecking': return `${t('job_comment_label_report')}:`
		case 'sendToReworking': return `${t('job_action_rework')}:`
		case 'endByAdmin': return `${t('job_comment_label_finish')}:`
		default: return ''
	}
})

const startAction = (action: JobAction) => {
	showCommentInput.value = true
	pendingAction.value = action
	commentMessage.value = ''
}

const directAction = async (action: 'endByWorker' | 'endByCreator' | 'endByAdmin') => {
	try {
		isLoading.value = true
		await store.changeJobStatus(props.job.Id, action)
	} finally {
		isLoading.value = false
	}
}

const submitAction = async () => {
	if (!pendingAction.value || !commentMessage.value.trim()) return
	const message = trimTrailingLineBreaks(commentMessage.value)
		.replace(/\n/g, '<br/>')
	const action = pendingAction.value
	cancelAction()
	try {
		isLoading.value = true
		await store.changeJobStatus(props.job.Id, action, message)
	} finally {
		isLoading.value = false
	}
}

const cancelAction = () => {
	showCommentInput.value = false
	commentMessage.value = ''
	pendingAction.value = null
	$commentTextarea.value?.reset()
}

const handleDelete = () => {
	$optionsDropdown.value?.close()
	$deleteJobDropdown.value?.open()
}

const onConfirmDelete = async () => {
	$deleteJobDropdown.value?.close()
	try {
		isLoading.value = true
		await store.deleteJob(props.job.Id)
	} finally {
		isLoading.value = false
	}
}

// Завершение работы администратором: с сообщением если NeedChecking, иначе сразу
const handleAdminEnd = () => {
	if (props.job.NeedChecking) {
		startAction('endByAdmin')
	} else {
		directAction('endByAdmin')
	}
}

const handleShowInfo = () => {
	$jobInfoModal.value?.onOpen(props.job.Id)
}

const searchText = computed(() => store.lastFilters.textFilter ?? '')

const buildMessageSegments = (message: string | null | undefined) =>
	splitMessageByBr(message).map(line =>
		splitTextByPhoneNumbers(line, searchText.value).map(el =>
			(el.isPhone || el.isEmail || el.isUrl)
				? el
				: { ...el, text: highlightSearchText(searchText.value, escapeHtml(el.text)) }
		)
	)

const messageSegments = computed(() => buildMessageSegments(props.job.Message))

const additionsSegments = computed(() =>
	(props.job.Additions ?? []).map(addition => ({
		addition,
		lines: buildMessageSegments(addition.Message)
	}))
)

const callPhone = async (phoneNumber: string) => {
	const clearNumber = encodeURIComponent(stripHighlightMarkup(phoneNumber))
	await PersonApi.callPhone(clearNumber)
}

const onEmail = (email: string) => {
	const link = document.createElement('a')
	link.href = `mailto:${stripHighlightMarkup(email)}`
	link.style.display = 'none'
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

const openUrlInNewTab = (url: string) => {
	window.open(normalizeUrlForOpening(stripHighlightMarkup(url)), '_blank', 'noopener,noreferrer')
}

const onCopyPhoneAndEmail = (text: string) => {
	const textArea = document.createElement('textarea')
	textArea.value = stripHighlightMarkup(text)
	textArea.style.position = 'fixed'
	textArea.style.left = '-999999px'
	textArea.style.top = '-999999px'
	document.body.appendChild(textArea)
	textArea.focus()
	textArea.select()
	try {
		document.execCommand('copy')
	} catch (err) {
		console.error('Не удалось скопировать текст: ', err)
	}
	document.body.removeChild(textArea)
}

</script>

<template>
	<file-info-modal
		ref="$fileInfoModal"
		:title="$t('file_information_and_access')"
		:rule-access="fileAccessLevel"
	/>
	<job-info-modal ref="$jobInfoModal" />
	<div
		ref="$job"
		:data-job-id="props.job.Id"
		:class="{
			'jobs-panel-list-item': true,
            'jobs-panel-list-item--is-dragging-over': isDraggingOver,
            'jobs-panel-list-item--can-drop': canDrop && isDraggingOver,
            'jobs-panel-list-item--cannot-drop': !canDrop && isDraggingOver
		}"
		@dragover="handleDragOver"
		@dragenter="handleDragEnter"
		@dragleave="handleDragLeave"
		@drop="handleDrop"
	>
		<app-loader v-if="isLoading" :style="loaderStyle"/>
		<div
			:class="{
				'jobs-panel-list-item__drop-zone': true,
				'jobs-panel-list-item__drop-zone--can-drop': canDrop && isDraggingOver,
				'jobs-panel-list-item__drop-zone--cannot-drop': !canDrop && isDraggingOver,
			}"
		/>
		<div class="jobs-panel-list-item__colored-wrap">
			<app-tooltip
				:text="jobStatusText"
				:disabled="!jobStatusText"
				position="left"
			>
				<template #activator>
					<div
						class="jobs-panel-list-item__colored"
						:style="{ backgroundColor: getJobStatusBarColor(props.job.Status) }"
					/>
				</template>
			</app-tooltip>
		</div>
		<div class="jobs-panel-list-item__header">
			<div class="jobs-panel-list-item__header-workers">
				<div
					class="jobs-panel-list-item__header-worker"
					@click="showUserJobs(props.job.Worker.Id)"
				>
					{{ props.job.Worker.Name }}
				</div>
				<template v-if="showCreatorName">
					<div class="jobs-panel-list-item__header-divider">
						<app-icon
							class="jobs-panel-list-item__header-options-btn"
							name="chevron-left"
							width="16px"
							color="#499259"
						/>
					</div>
					<div
						class="jobs-panel-list-item__header-worker"
						@click="showUserJobs(props.job.Creator.Id)"
					>
						{{ props.job.Creator.Name }}
					</div>
				</template>
			</div>
			<div class="jobs-panel-list-item__header-options">
				<div v-if="hasActionButtons" class="jobs-panel-list-item__actions">
					<!-- Случай 1: Исполнитель, с отчётом -->
					<template v-if="showSendToChecking">
						<app-tooltip :text="$t('job_action_send_to_checking')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--warning"
									name="send-to-check"
									width="18px"
									color="#FEF3D5"
									cursor="pointer"
									@click="startAction('sendToChecking')"
								/>
							</template>
						</app-tooltip>
					</template>
					<!-- Случай 2: Исполнитель, без отчёта -->
					<template v-if="showEndByWorkerNoMessage">
						<app-tooltip :text="$t('job_action_finish')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--success"
									name="job-finish"
									width="18px"
									color="#D9E7DC"
									cursor="pointer"
									@click="directAction('endByWorker')"
								/>
							</template>
						</app-tooltip>
					</template>
					<!-- Случай 3: Исполнитель = Создатель, с отчётом -->
					<template v-if="showSelfWorkerWithReport">
						<app-tooltip :text="$t('job_action_finish_with_report')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--success"
									name="job-finish"
									width="18px"
									color="#D9E7DC"
									cursor="pointer"
									@click="startAction('endByWorker')"
								/>
							</template>
						</app-tooltip>
					</template>
					<!-- Случай 4: Исполнитель = Создатель, без отчёта -->
					<template v-if="showSelfWorkerNoReport">
						<app-tooltip :text="$t('job_action_finish')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--success"
									name="job-finish"
									width="18px"
									color="#D9E7DC"
									cursor="pointer"
									@click="directAction('endByWorker')"
								/>
							</template>
						</app-tooltip>
					</template>
					<!-- Случай 5: Инициатор проверяет -->
					<template v-if="showCreatorChecking">
						<app-tooltip :text="$t('job_action_accept')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--success"
									name="job-finish"
									width="18px"
									color="#D9E7DC"
									cursor="pointer"
									@click="directAction('endByCreator')"
								/>
							</template>
						</app-tooltip>
						<app-tooltip :text="$t('job_action_rework')">
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__action-icon--warning"
									name="back-to-rework"
									width="18px"
									color="#FEF3D5"
									cursor="pointer"
									@click="startAction('sendToReworking')"
								/>
							</template>
						</app-tooltip>
					</template>
				</div>
				<app-resizable-dropdown
					ref="$optionsDropdown"
					position="right"
					trigger="hover"
					:hover-delay="100"
					:mobile-title="$t('contact_choose_action')"
				>
					<template #activator>
						<app-icon
							class="jobs-panel-list-item__header-options-btn"
							name="options-menu"
							height="18px"
							width="22px"
							color="#979797"
							hover-color="#3F884F"
							cursor="pointer"
						/>
					</template>
					<jobs-options-dropdown
						:job="props.job"
						:show-send-to-checking="showSendToChecking"
						:show-end-by-worker="showEndByWorkerNoMessage || showSelfWorkerNoReport"
						:show-end-by-worker-with-message="showSelfWorkerWithReport"
						:show-end-by-creator="showCreatorChecking"
						:show-send-to-reworking="showCreatorChecking"
						:show-end-by-admin="showAdminEnd || showAdminEndExtra"
						:show-edit="canEditJob"
						:show-delete="canDeleteJob"
						@on-send-to-checking="startAction('sendToChecking')"
						@on-end-by-worker="directAction('endByWorker')"
						@on-end-by-worker-with-message="startAction('endByWorker')"
						@on-end-by-creator="directAction('endByCreator')"
						@on-send-to-reworking="startAction('sendToReworking')"
						@on-end-by-admin="handleAdminEnd"
						@on-show-info="handleShowInfo"
						@on-edit="handleEdit"
						@on-delete="handleDelete"
					/>
				</app-resizable-dropdown>
				<app-resizable-dropdown
					ref="$editJobDropdown"
					name="$editJobDropdown"
					position="right"
					resizable
					:initial-width="320"
					:initial-height="398"
					:min-width="320"
					:max-width="480"
					:min-height="398"
					:max-height="600"
					:close-by-click-outside="false"
					:hide-by-parent-scroll="false"
					:mobile-title="$t('job_edit_title')"
				>
					<template #activator>
						<span class="jobs-panel-list-item__edit-anchor" />
					</template>
					<create-edit-job-dropdown
						type="edit"
						:job="props.job"
						:workers="optionsPersons"
						@on-close="$editJobDropdown?.close()"
					/>
				</app-resizable-dropdown>
				<app-resizable-dropdown
					ref="$deleteJobDropdown"
					position="right"
					centered-on-mobile
				>
					<template #activator>
						<span class="jobs-panel-list-item__edit-anchor" />
					</template>
					<delete-job-confirm-dropdown
						@cancel="$deleteJobDropdown?.close()"
						@confirm="onConfirmDelete"
					/>
				</app-resizable-dropdown>
			</div>
		</div>
		<div class="jobs-panel-list-item__body">
			<div class="jobs-panel-list-item__body-message">
				<template v-for="(segments, lineIdx) in messageSegments" :key="lineIdx">
					<template v-for="(el, elIdx) in segments" :key="elIdx">
						<app-resizable-dropdown
							v-if="el.isPhone"
							position="bottom"
							trigger="hover"
							:initial-width="125"
							:initial-height="62"
							:mobile-title="$t('contact_choose_action')"
						>
							<template #activator>
								<span
									class="jobs-panel-list-item__body-phone"
									@click.stop="callPhone(el.text)"
									v-html="el.text"
								/>
							</template>
							<contacts-email-phone-options-dropdown
								type="phone"
								@on-call="callPhone(el.text)"
								@on-copy="onCopyPhoneAndEmail(el.text)"
							/>
						</app-resizable-dropdown>
						<app-resizable-dropdown
							v-else-if="el.isEmail"
							position="bottom"
							trigger="hover"
							:initial-width="125"
							:initial-height="62"
							:mobile-title="$t('contact_choose_action')"
						>
							<template #activator>
								<a
									class="jobs-panel-list-item__body-email"
									@click.stop.prevent="onEmail(el.text)"
									v-html="el.text"
								/>
							</template>
							<contacts-email-phone-options-dropdown
								type="email"
								@on-email="onEmail(el.text)"
								@on-copy="onCopyPhoneAndEmail(el.text)"
							/>
						</app-resizable-dropdown>
						<a
							v-else-if="el.isUrl"
							class="jobs-panel-list-item__body-url"
							:href="normalizeUrlForOpening(stripHighlightMarkup(el.text))"
							target="_blank"
							rel="noopener noreferrer"
							@click.stop.prevent="openUrlInNewTab(el.text)"
							v-html="el.text"
						/>
						<span v-else v-html="el.text" />
					</template>
					<br v-if="lineIdx < messageSegments.length - 1" />
				</template>
			</div>
			<!-- Additions: свёрнутое состояние (>= 2 комментариев) -->
			<div
				v-if="props.job.Additions?.length >= 2 && !additionsExpanded"
				class="jobs-panel-list-item__additions-collapsed"
				@click="additionsExpanded = true"
			>
				<app-icon name="plus-circle" width="16px" color="#979797" cursor="pointer" />
				<span>{{ $tc('job_comments_count', props.job.Additions.length ?? 0) }}</span>
			</div>
			<!-- Additions: развёрнутое состояние или единственный комментарий -->
			<div
				v-else-if="props.job.Additions?.length"
				class="jobs-panel-list-item__additions"
			>
				<div
					v-for="item in additionsSegments"
					:key="item.addition.Date"
					class="jobs-panel-list-item__addition"
				>
					<div class="jobs-panel-list-item__addition-header">
						<span class="jobs-panel-list-item__addition-user">{{ item.addition.Username }}</span>
						<span class="jobs-panel-list-item__addition-date">{{ formatDateTime(item.addition.Date) }}</span>
					</div>
					<div class="jobs-panel-list-item__addition-message">
						<b class="jobs-panel-list-item__addition-title">{{ item.addition.Title }}</b>&nbsp;<span>
							<template v-for="(segments, lineIdx) in item.lines" :key="lineIdx">
								<template v-for="(el, elIdx) in segments" :key="elIdx">
									<app-resizable-dropdown
										v-if="el.isPhone"
										position="bottom"
										trigger="hover"
									:initial-width="125"
									:initial-height="62"
									:mobile-title="$t('contact_choose_action')"
									>
										<template #activator>
											<span
												class="jobs-panel-list-item__body-phone"
												@click.stop="callPhone(el.text)"
												v-html="el.text"
											/>
										</template>
										<contacts-email-phone-options-dropdown
											type="phone"
											@on-call="callPhone(el.text)"
											@on-copy="onCopyPhoneAndEmail(el.text)"
										/>
									</app-resizable-dropdown>
									<app-resizable-dropdown
										v-else-if="el.isEmail"
										position="bottom"
										trigger="hover"
									:initial-width="125"
									:initial-height="62"
									:mobile-title="$t('contact_choose_action')"
									>
										<template #activator>
											<a
												class="jobs-panel-list-item__body-email"
												@click.stop.prevent="onEmail(el.text)"
												v-html="el.text"
											/>
										</template>
										<contacts-email-phone-options-dropdown
											type="email"
											@on-email="onEmail(el.text)"
											@on-copy="onCopyPhoneAndEmail(el.text)"
										/>
									</app-resizable-dropdown>
									<a
										v-else-if="el.isUrl"
										class="jobs-panel-list-item__body-url"
										:href="normalizeUrlForOpening(stripHighlightMarkup(el.text))"
										target="_blank"
										rel="noopener noreferrer"
										@click.stop.prevent="openUrlInNewTab(el.text)"
										v-html="el.text"
									/>
									<span v-else v-html="el.text" />
								</template>
								<br v-if="lineIdx < item.lines.length - 1" />
							</template>
						</span>
					</div>
				</div>
			</div>
			<div v-if="showCommentInput" class="jobs-panel-list-item__comment" @keydown.escape="cancelAction">
				<span class="jobs-panel-list-item__comment-label">{{ commentLabel }}</span>
				<div class="jobs-panel-list-item__comment-input">
					<app-custom-textarea
						ref="$commentTextarea"
						v-model="commentMessage"
						:placeholder="$t('ui_enter_text')"
						:min-height="32"
						:max-height="200"
						:auto-focus="true"
						:new-line-mode="newLineMode"
						@enter="submitAction"
						@ctrl-enter="submitAction"
					/>
					<app-tooltip :text="$t('ui_cancel')">
						<template #activator>
							<app-icon
								name="close-bold"
								width="16px"
								color="#979797"
								hover-color="#BA4C4C"
								cursor="pointer"
								@click="cancelAction"
							/>
						</template>
					</app-tooltip>
					<app-tooltip :text="$t(sendHintKey)">
						<template #activator>
							<app-icon
								name="send-message"
								width="16px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="submitAction"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>
			<div
				v-if="props.job.Project && !isProjectContext"
				class="jobs-panel-list-item__body-project"
			>
				<span class="jobs-panel-list-item__body-project-text">{{ $t('job_project_short') }}:&nbsp;</span>
				<router-link
					class="jobs-panel-list-item__body-project-link"
					:to="{ query: { page: 'project', id: props.job.Project.Id } }"
				>
					<span v-html="highlightSearchText(searchText, escapeHtml(props.job.Project.Name))" />
				</router-link>
			</div>
			<div
				v-if="visibleClients.length"
				class="jobs-panel-list-item__body-project"
			>
				<span class="jobs-panel-list-item__body-project-text">
					{{ $t('job_client_short') }}:
				</span>
				<template
					v-for="(client, index) in displayedClients"
					:key="client.Id"
				>
					<router-link
						class="jobs-panel-list-item__body-project-link"
						:to="{ query: { page: 'client', id: client.Id } }"
					>
						<span v-html="highlightSearchText(searchText, escapeHtml(client.Name))" />
					</router-link>
					<span
						v-if="index + 1 < displayedClients.length"
						class="jobs-panel-list-item__body-project-divider"
					>&nbsp;/&nbsp;</span>
				</template>
				<span
					v-if="!isClientsExpanded && visibleClients.length > 1"
					class="jobs-panel-list-item__body-project-show-more"
					@click="isClientsExpanded = true"
				>
					{{ $t('more_count_label', { count: visibleClients.length - 1 }) }}
				</span>
			</div>
		</div>
		<div class="jobs-panel-list-item__footer">
			<div class="jobs-panel-list-item__footer-left">
				<div :class="{
					'jobs-panel-list-item__footer-left-options': true,
					'jobs-panel-list-item__footer-left-options--have-files': props.job.Files?.length,
				}">
					<div
						class="jobs-panel-list-item__footer-left-options-files-block"
						:style="{ marginTop:  props.job.Files?.length ? '4px' : '0' }"
					>
						<input
							ref="$uploadFile"
							type="file"
							class="upload-block__input"
							multiple
							@change="handleFileInput"
						/>
						<app-tooltip
							v-if="!isJobsReadonly && canManageJobFiles"
							:text="$t('file_upload')"
						>
							<template #activator>
								<app-icon
									class="jobs-panel-list-item__header-options-btn jobs-panel-list-item__attach-btn"
									:class="{ 'jobs-panel-list-item__attach-btn--hover-only': !hasAttachments }"
									name="clip"
									width="18px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									@click="handleClick"
								/>
							</template>
						</app-tooltip>
						<app-icon
							v-else
							class="jobs-panel-list-item__header-options-btn jobs-panel-list-item__attach-btn"
							:class="{ 'jobs-panel-list-item__attach-btn--hover-only': !hasAttachments }"
							name="clip"
							width="18px"
							color="#D2D2D2"
						/>
						<div
							v-if="props.job.Files?.length"
							class="jobs-panel-list-item__footer-left-options-files-block-items"
						>
							<div
								v-for="
									(file, index) in !showAllFiles
										? props.job.Files.slice(0, 2)
										: props.job.Files
								"
								class="jobs-panel-list-item__footer-left-options-files-block-item"
								:key="file.Id"
							>
								<app-tooltip :text="$t('information')">
									<template #activator>
										<app-icon
											class="jobs-panel-list-item__header-options-btn"
											:style="{ marginRight: '2px' }"
											name="info-circle-outline"
											width="18px"
											:color="hasFileAccess ? '#979797' : '#D2D2D2'"
											:hover-color="hasFileAccess ? '#499259' : undefined"
											:cursor="hasFileAccess ? 'pointer' : 'default'"
											@click="showFileInfo(file)"
										/>
									</template>
								</app-tooltip>
								<div
									class="jobs-panel-list-item__footer-left-options-files-block-item-name"
									:class="{'jobs-panel-list-item__footer-left-options-files-block-item-name-disable': !hasFileAccess || file.Editable === false}"
									@click="downloadFile(file)"
								>
									{{ file.Name }}
								</div>
								<app-tooltip v-if="!isJobsReadonly && hasFileAccess && file.Editable" :text="$t('file_detach')">
									<template #activator>
										<app-icon
											class="jobs-panel-list-item__footer-left-options-tag-delete"
											:style="{ marginRight: '4px', marginLeft: '2px' }"
											name="close-bold"
											width="12px"
											color="#D2D2D2"
											hover-color="#BA4C4C"
											cursor="pointer"
											@click="detachFile(file)"
										/>
									</template>
								</app-tooltip>
								<app-tooltip
									v-if="hasFileAccess && getFileLocationTarget(props.job)"
									:text="$t('file_go_to_location')"
								>
									<template #activator>
										<app-icon
											class="jobs-panel-list-item__header-options-btn"
											name="arrow-45deg"
											width="18px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
											@click="goToFileLocation(file)"
										/>
									</template>
								</app-tooltip>
								<div
									v-if="props.job.Files.length > 2 && index === 1 && !showAllFiles"
									class="jobs-panel-list-item__footer-left-options-files-block-item-more"
									@click="showAllFiles = true"
								>
									{{ $t('more_count_label', { count: props.job.Files.length - 2 }) }}
								</div>
							</div>
						</div>
					</div>
					<div class="jobs-panel-list-item__footer-left-options-tags-block">
						<app-resizable-dropdown
							v-if="!isJobsReadonly"
							ref="$addTagDropdown"
							position="right"
							:close-by-click-outside="true"
							:max-width="640"
							:max-height="460"
							:mobile-title="$t('choose_tags')"
							@open="isTagDropdownOpen = true"
							@close="isTagDropdownOpen = false"
						>
							<template #activator>
								<app-tooltip :text="$t('file_add_tag')">
									<template #activator>
										<app-icon
											:class="{
												'jobs-panel-list-item__header-options-btn': true,
												'jobs-panel-list-item__attach-btn': true,
												'jobs-panel-list-item__attach-btn--hover-only': !hasAttachments,
												'jobs-panel-list-item__attach-btn--active': isTagDropdownOpen,
											}"
											name="hash"
											width="18px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
										/>
									</template>
								</app-tooltip>
							</template>
							<app-tag-selector
								:model-value="jobTagsModel"
								:entity-type="TagEntityType.Job"
								mode="select"
								allow-create
								@update:model-value="onTagSelectorChange"
								@create-tag="onCreateTag"
								@close="$addTagDropdown?.close()"
							/>
						</app-resizable-dropdown>
						<app-icon
							v-else
							class="jobs-panel-list-item__header-options-btn jobs-panel-list-item__attach-btn"
							:class="{ 'jobs-panel-list-item__attach-btn--hover-only': !hasAttachments }"
							name="hash"
							width="18px"
							color="#979797"
						/>
						<div
							v-if="props.job.Tags && props.job.Tags.length"
							class="jobs-panel-list-item__footer-left-options-tags"
						>
							<template v-if="!isJobsReadonly">
								<div
									v-for="tag in props.job.Tags"
									:key="tag.Id"
									class="jobs-panel-list-item__footer-left-options-tag"
								>
									<span class="jobs-panel-list-item__footer-left-options-tag-name">{{ tag.Name.toLowerCase() }}</span>
									<app-tooltip :text="$t('ui_tag_detach')">
										<template #activator>
											<app-icon
												class="jobs-panel-list-item__footer-left-options-tag-delete"
												:style="{ marginLeft: '2px' }"
												name="close-bold"
												width="12px"
												color="#D2D2D2"
												hover-color="#BA4C4C"
												cursor="pointer"
												@click="onDetachTag(tag.Id)"
											/>
										</template>
									</app-tooltip>
								</div>
							</template>
							<span
								v-else
								class="jobs-panel-list-item__footer-left-options-tags-plain"
							>
								{{ tagsReadonlyText }}
							</span>
						</div>
						<div
							v-if="isSystemJob && !hasAttachments"
							class="jobs-panel-list-item__footer-left-options-date"
						>
							{{ formatTimeDate(props.job.CreateTime) }}
						</div>
					</div>
				</div>
			</div>
			<div v-if="!isSystemJob || hasAttachments" class="jobs-panel-list-item__footer-bottom">
				<app-tooltip v-if="!isSystemJob" :text="dateTooltip">
					<template #activator>
						<div
							:class="{
								'jobs-panel-list-item__footer-bottom-date': true,
								'jobs-panel-list-item__footer-bottom-date--lost': isDateLost,
								'jobs-panel-list-item__footer-bottom-date--success': isDateSuccess
							}"
						>
							<app-icon
								class="jobs-panel-list-item__footer-bottom-date-clock"
								name="clock"
								width="16px"
							/>
							<template v-if="props.job.CompletionDate">
								{{ formatTimeDate(props.job.CompletionDate) }}
							</template>
							<template v-else-if="props.job.ToDoDate">
								{{ formatDate(props.job.ToDoDate) }}
							</template>
						</div>
					</template>
				</app-tooltip>
				<span class="jobs-panel-list-item__footer-bottom-created">{{ formatTimeDate(props.job.CreateTime) }}</span>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
@import "@/styles/color.scss";

@keyframes jobHighlightBlink {
	0%, 33.33%, 66.66%, 100% { background-color: transparent; }
	16.66%, 50%, 83.33%      { background-color: #F3F8F4; }
}

.jobs-panel-list-item {
	display: flex;
	flex-direction: column;
	position: relative;
	padding: 0 8px 0 16px;
	color: #4D4D4D;
	border: 1px dashed transparent;
	border-left: none !important;
	border-radius: 8px;

	&--highlight {
		animation: jobHighlightBlink 1.8s ease-in-out;
	}

	&--can-drop {
		border-color: #3F884F;
	}

	&--cannot-drop {
		border-color: #D05A4F;
	}

	&__drop-zone {
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: -1;

		&--can-drop {
			z-index: 1;
			background-color: rgba(#3F884F, 0.05);
		}

		&--cannot-drop {
			z-index: 1;
			background-color: rgba(#D05A4F, 0.05);
		}

		&-text {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 16px;
			color: #fff;
			font-size: 14px;
			line-height: 18px;
			font-weight: 500;
			border-radius: 8px;
		}
	}

	&__colored-wrap {
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 4px;

		:deep(.app-tooltip),
		:deep(.app-tooltip__activator) {
			display: block;
			width: 100%;
			height: 100%;
		}
	}

	&__colored {
		width: 100%;
		height: 100%;
		border-top-left-radius: 8px 12px;
		border-bottom-left-radius: 8px 12px;
	}

	&__header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 8px;

		&-workers {
			display: inline;
			color: #438451;
			font-weight: 500;
			font-size: 14px;
			line-height: 18px;
		}

		&-worker {
			display: inline;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}

		&-divider {
			display: inline;
		}

		&-options {
			display: flex;
			align-items: center;
		}
	}

	&__edit-anchor {
		display: inline-block;
		width: 0;
		height: 0;
	}

	&__body {
		display: flex;
		flex-direction: column;
		margin-bottom: 4px;

		&-message {
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			margin-bottom: 4px;
			word-break: break-word;
		}

		&-phone {
			color: #438451;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}

		&-email {
			color: #438451;
			cursor: pointer;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}

		&-url {
			color: #438451;
			cursor: pointer;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}

		&-project {
			display: inline;
			line-height: 18px;

			&-text {
				font-size: 14px;
				margin-right: 2px;
				color: #333333;
			}

			&-link {
				display: inline;
				color: #438451;
				text-decoration: none;
				cursor: pointer;
				font-size: 14px;
				margin-right: 4px;

				&:hover {
					text-decoration: underline;
				}
			}

			&-divider {
				display: inline;
				color: #4D4D4D;
				font-weight: 500;
				font-size: 14px;
			}

			&-show-more {
				display: inline-block;
				background-color: #F2F2F2;
				font-size: 12px;
				line-height: 16px;
				padding: 0 4px;
				border-radius: 8px;
				cursor: pointer;
				color: #979797;

				&:hover {
					background-color: #F3F8F4;
					color: #438451;
				}
			}
		}
	}

	&__footer {
		display: flex;
		flex-direction: column;
		max-width: 100%;

		&-left {
			display: flex;
			flex-direction: column;
			gap: 8px;
			max-width: 100%;

			&-options {
				display: flex;
				max-width: 100%;

				&--have-files {
					flex-direction: column-reverse;
				}

				&-files-block {
					display: flex;
					gap: 2px;
					max-width: 100%;

					&-items {
						display: flex;
						flex-direction: column;
						gap: 4px;
						max-width: calc(100% - 20px);
					}

					&-item {
						display: flex;
						align-items: center;

						&-name {
							text-overflow: ellipsis;
							overflow: hidden;
							white-space: nowrap;
							margin-right: 2px;
							color: $color-text-secondary;
							font-size: 14px;
							line-height: 18px;
							cursor: pointer;

							&-disable {
								color: $color-text_unavailable;
								cursor: default;
							}
						}

						&-more {
							display: flex;
							align-items: center;
							justify-content: center;
							white-space: nowrap;
							margin-left: 4px;
							padding: 0 4px;
							border-radius: 8px;
							background-color: #F2F2F2;
							color: #979797;
							font-size: 12px;
							line-height: 16px;
							cursor: pointer;

							&:hover {
								background-color: #F3F8F4;
								color: #438451;
							}
						}
					}
				}

				&-tags-block {
					display: flex;
					flex: 1 1 auto;
					align-items: flex-start;
					gap: 4px;
					min-width: 0;
					font-size: 14px;
					line-height: 18px;
				}

				&-date {
					flex: 0 0 auto;
					align-self: flex-end;
					margin-left: auto;
					color: #979797;
					font-size: 12px;
					line-height: 16px;
					white-space: nowrap;
				}

				&-tags {
					flex: 1 1 0;
					min-width: 0;

					&-plain {
						color: #979797;
						word-break: break-word;
					}
				}

				&-tag {
					display: inline;
					margin-right: 4px;
					color: #979797;

					&-name {
						word-break: break-all;
					}

					&-delete {
						margin: -1px 2px 0 -2px;
					}
				}
			}
		}

		&-bottom {
			display: flex;
			align-items: flex-end;
			justify-content: space-between;
			margin-top: 4px;
			font-size: 12px;
			line-height: 16px;
			color: #979797;

			:deep(.app-tooltip__activator) {
				display: flex;
			}

			&-created {
				margin-left: auto;
				color: #979797;
				font-size: 12px;
				line-height: 16px;
			}

			&-date {
				display: flex;
				align-items: center;
				color: #979797;
				font-size: 12px;
				line-height: 16px;

				&--lost {
					color: #EE966A !important;

					svg {
						path {
							color: #EE966A !important;
						}
					}
				}

				&--success {
					color: #6DA279 !important;

					svg {
						path {
							color: #6DA279 !important;
						}
					}
				}

				&-clock {
					cursor: auto;
					flex-shrink: 0;
					margin-top: -2px;
					margin-right: 4px;

					&:hover {
						background: transparent;
					}
				}
			}
		}
	}

	&__attach-btn--hover-only {
		visibility: hidden;
		opacity: 0;
		transition: opacity 0.2s;
	}

	&:hover &__attach-btn--hover-only,
	&__attach-btn--active {
		visibility: visible;
		opacity: 1;
	}

	@media (hover: none) {
		&__attach-btn--hover-only {
			visibility: visible;
			opacity: 1;
		}
	}

	&:hover &__action-icon--warning {
		color: #F9C22F !important;
	}

	&:hover &__action-icon--success {
		color: #3F884F !important;
	}

	@media (hover: none) {
		&__action-icon--warning {
			color: #F9C22F !important;
		}

		&__action-icon--success {
			color: #3F884F !important;
		}
	}

	&__additions-collapsed {
		display: flex;
		align-items: center;
		gap: 2px;
		padding: 4px 4px 8px 0;
		cursor: pointer;
		color: #979797;
		font-size: 12px;
		line-height: 16px;

		&:hover {
			color: #666;
		}
	}

	&__additions {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 4px;
	}

	&__addition {
		display: flex;
		flex-direction: column;
		gap: 2px;

		&-header {
			display: flex;
			align-items: baseline;
			gap: 6px;
		}

		&-user {
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			font-weight: 400;
			font-size: 14px;
			line-height: 18px;
			color: #4B7053;
		}

		&-date {
			flex-shrink: 0;
			font-size: 12px;
			line-height: 16px;
			color: #2A232380;
			white-space: nowrap;
		}

		&-message {
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			word-break: break-word;
		}

		&-title {
			font-weight: 400;
			color: #979797;
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-right: 2px;
	}

	&__comment {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;
		margin-bottom: 4px;

		&-label {
			margin-top: 4px;
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
			color: #979797;
		}

		&-input {
			display: flex;
			align-items: center;
			gap: 4px;

			> :deep(.app-tooltip > .app-tooltip__activator) {
				display: flex;
			}
		}
	}

	&__body-message,
	&__addition-message {
		:deep(.dropdown-activator) {
			display: inline-block;
			vertical-align: baseline;
		}
	}

}

.upload-block__input {
	display: none;
}

@media (max-width: 480px) {
	.jobs-panel-list-item__actions {
		display: none;
	}
}
</style>
